import React, { ReactNode } from 'react';

export interface MobileRowContentProps {
  /** Line 1: the row identifier (plate number, name, invoice id). */
  title: ReactNode;
  /** Line 1, right-aligned: usually a `FeedbackStatus` pill. */
  status?: ReactNode;
  /** Line 2: short context fields, joined with ` · ` and truncated. */
  meta?: ReactNode[];
  className?: string;
}

/**
 * Standard two-line body for a `TableData.mobileRow` (Template 1). Pages
 * build their row in one line:
 *
 * ```tsx
 * mobileRow={(v) => (
 *   <MobileRowContent
 *     title={v.plate}
 *     status={<FeedbackStatus status={v.status} />}
 *     meta={[v.model, v.hub]}
 *   />
 * )}
 * ```
 */
export const MobileRowContent: React.FC<MobileRowContentProps> = ({
  title,
  status,
  meta,
  className = '',
}) => {
  const metaItems = (meta ?? []).filter(
    (item) => item !== null && item !== undefined && item !== ''
  );

  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className}`.trim()}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-gray-900">
          {title}
        </span>
        {status && <span className="shrink-0">{status}</span>}
      </div>
      {metaItems.length > 0 && (
        <p className="truncate text-[13px] text-gray-600">
          {metaItems.map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              {item}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

export default MobileRowContent;
